import { StudioAddress } from '@/components/StudioAddress'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import type { StudioSection } from '@/features/scheduling/availability'
import { Pressable } from '@/ui/core/pressable'
import { Sheet, SheetTitle } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import * as Linking from 'expo-linking'
import { View } from 'react-native'

/** Tall enough to be the sheet's subject, short enough to leave the address room under it. */
const PANEL_HEIGHT = 210

/**
 * The way to the studio, in the map the phone already has.
 *
 * No map is drawn here. An embedded one is either a key the app has to carry and renew, or a
 * native map engine pinching and panning inside a sheet whose own gesture is to drag down and
 * close — and neither buys anything over the map the member already has installed, knows how to
 * use, and can navigate from. So this is the shape a map would have been, and pressing it opens
 * Apple Maps on iOS and whatever Android has been set to.
 *
 * It does not imitate a map. A street grid that is not this street, under a pin that is not this
 * door, is a lie told to someone trying to find a building.
 */
const MapWay = ({ name, onOpen }: { name: string; onOpen: () => void }) => (
	<Pressable
		onPress={onOpen}
		accessibilityRole="button"
		accessibilityLabel={`Open ${name} in Maps`}
		className="items-center justify-center rounded-note bg-blush"
		style={{ height: PANEL_HEIGHT, gap: 10 }}
	>
		<View className="h-12 w-12 items-center justify-center rounded-full bg-white">
			<Glyph name="pin" size={22} color={colors.ink} />
		</View>
		<Text size="body1" weight="bold">
			Open in Maps
		</Text>
	</Pressable>
)

/**
 * Where a studio is: the way there, then the address.
 *
 * Two objects. The panel carries the action and says so on its face, so the text link that used
 * to sit under the address was the same destination twice — and the second one was the smaller
 * thing to aim at.
 */
export const StudioSheet = ({
	studio,
	onClose,
}: { studio?: StudioSection; onClose: () => void }) => (
	<Sheet open={!!studio} onClose={onClose}>
		{!!studio && (
			<View className="pt-2">
				<SheetTitle glyph="pin">{studio.name}</SheetTitle>
				<VStack gap={16} className="px-4 pb-8">
					<MapWay name={studio.name} onOpen={() => Linking.openURL(studio.mapUrl)} />
					<StudioAddress address={studio.structuredAddress} siteName={studio.siteName} />
				</VStack>
			</View>
		)}
	</Sheet>
)
